import { useEffect, useState } from "react";
import { Mail, MessageCircle, History } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const KIND = { ricevuto: "Messaggio dal cliente", ricevuta: "Ricevuta", otp_login: "OTP accesso", otp_ritiro: "OTP ritiro", ritiro: "Ritiro", promemoria: "Promemoria", account: "Account", email: "Email", regolamento: "Regolamento aggiornato", rules_updated: "Regolamento aggiornato", rules: "Regolamento",
  win: "Vincita", no_prize: "Nessun premio", reminder: "Promemoria", campaign: "Campagna", discount: "Sconto", receipt: "Ricevuta", redeem_otp: "OTP ritiro", redeemed: "Ritiro", consent: "Consenso", stop: "Stop" };
const STATUS = { received: ["Ricevuto", "bg-violet-500/15 text-violet-300 border-violet-500/30"], queued: ["In coda (notte)", "bg-amber-500/15 text-amber-300 border-amber-500/30"], sent: ["Inviato", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"], delivered: ["Consegnato", "bg-teal-500/15 text-teal-300 border-teal-500/30"], read: ["Letto", "bg-sky-500/15 text-sky-300 border-sky-500/30"], simulated: ["Simulato", "bg-sky-500/15 text-sky-300 border-sky-500/30"], skipped: ["Non inviato", "bg-slate-500/15 text-slate-300 border-slate-500/30"], failed: ["Fallito", "bg-red-500/15 text-red-400 border-red-500/30"] };

const DEFAULT_STATUS = (st) => [st || "—", "bg-slate-500/15 text-slate-300 border-slate-500/30"];

function ChannelIcon({ it }) {
  if (it.channel === "email") return <Mail className="h-3.5 w-3.5 mt-0.5 text-amber-400 shrink-0" />;
  if (it.direction === "in") return <MessageCircle className="h-3.5 w-3.5 mt-0.5 text-violet-400 shrink-0 -scale-x-100" />;
  return <MessageCircle className="h-3.5 w-3.5 mt-0.5 text-emerald-400 shrink-0" />;
}

function DeliveryLine({ it }) {
  if (!it.delivered_at && !it.read_at) return null;
  const parts = [it.delivered_at && `✓ consegnato ${fmt(it.delivered_at)}`, it.read_at && `✓✓ letto ${fmt(it.read_at)}`].filter(Boolean);
  return <p className="text-muted-foreground/80 mt-0.5">{parts.join(" · ")}</p>;
}

function TimelineItem({ it }) {
  const st = STATUS[it.status] || DEFAULT_STATUS(it.status);
  return (
    <div className="rounded-lg border border-border bg-card p-2.5 text-xs flex gap-2 items-start" data-testid={`timeline-item-${it.id}`}>
      <ChannelIcon it={it} />
      <div className={`flex-1 min-w-0 ${it.direction === "in" ? "border-l-2 border-violet-500/40 pl-2" : ""}`}>
        <div className="flex items-center gap-2"><span className="font-medium">{KIND[it.kind] || it.kind}</span><span className="text-muted-foreground truncate">{it.to}</span><Badge className={`border ml-auto ${st[1]}`}>{st[0]}</Badge></div>
        {it.subject && <p className="text-muted-foreground truncate mt-0.5" title={it.subject}>{it.subject}</p>}
        {it.error && <p className="text-red-400 mt-0.5">{it.error}</p>}
        <DeliveryLine it={it} />
        <p className="text-muted-foreground/70 mt-0.5">{fmt(it.created_at)}</p>
      </div>
    </div>
  );
}

export const CustomerTimeline = ({ customerId }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { setItems(null); api.customerTimeline(customerId).then(setItems).catch(() => setItems([])); }, [customerId]);
  return (
    <div data-testid="customer-timeline">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><History className="h-4 w-4 text-violet-400" /> Timeline contatti <span className="text-xs text-muted-foreground font-normal">{items ? `(${items.length})` : ""}</span></h3>
      {!items && <p className="text-xs text-muted-foreground">Caricamento…</p>}
      {items?.length === 0 && <p className="text-xs text-muted-foreground">Nessun contatto inviato</p>}
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {(items || []).map((it) => <TimelineItem key={it.id} it={it} />)}
      </div>
    </div>
  );
};
